import typia from 'typia';
import type { ViewIds } from '@ardoq/api-types';
import { type BlocksArrangement, type BlocksArrangementRecipe, mergeArrangements, parseArrangementRecipe, parseArrangements, } from 'tabview/assistantBlocks/arrangementRecipe';
import { AiControlledCommandId } from 'aiControlledCommands/aiControlledCommandId';
import type { CommandValidationResult } from 'aiControlledCommands/defineAiControlledCommand';
import type { CommandExecutionState } from 'aiControlledCommands/types';
import { VIEWPOINT_DOMAIN } from 'aiControlledCommands/viewpoint/domain';
import type { BlocksNode } from 'tabview/visualizations/aq-blocks/types';
import { BLOCKS_CONTEXT_DESCRIPTION } from './descriptions';
import { type BlocksContext, buildGroupTree, describeAlignmentProblems, } from './context';
import { effectiveArrangements, resolveContainerArrangements, } from 'tabview/assistantBlocks/resolveArrangements';
import { type ArrangedLayoutReport, describeArrangedLayout, } from './layoutReport';
import type { EnhancedScopeData } from '@ardoq/data-model';
import type { ResolvedArrangement } from 'tabview/assistantBlocks/compileArrangement';
import type { GroupLink } from 'tabview/assistantBlocks/groupLinks';
/** The sibling groups whose inner blocks a view lines up, for the account to describe. */
export type FindGroupLinks = (args: {
    scopeData: EnhancedScopeData;
    root: BlocksNode;
    resolved: ResolvedArrangement[];
}) => GroupLink[];
/** The views whose layout the assistant arranges with a recipe. */
export type ArrangeableViewId = ViewIds.ASSISTANT_BLOCKS | ViewIds.BLOCKS;
export type BlocksCommandContext = BlocksContext & {
    title: string | null;
    arrangements: BlocksArrangement[];
};
export type ResolvedBlocksLayout = {
    recipe: BlocksArrangementRecipe;
    expectedArrangements: BlocksArrangement[];
    report: ArrangedLayoutReport;
};
const RECIPE_KEYS = ['title', 'arrangements'] as const;
const describeInvalidRecipe = (rawPayload: unknown): string => {
    const missing = RECIPE_KEYS.filter(key => typeof rawPayload !== 'object' ||
        rawPayload === null ||
        !(key in rawPayload));
    if (missing.length) {
        return `Not applied: the layout is missing ${missing.join(' and ')}. Send them at the top level of the tool's arguments, beside action_description.`;
    }
    return "Not applied: the layout doesn't match the JSON schema in the tool description. Send every region field, with null where it's unused.";
};
type ToSavedArrangementsArgs = {
    recipe: BlocksArrangementRecipe;
    viewId: ArrangeableViewId;
    state: CommandExecutionState;
    root: BlocksNode;
};
/** What a recipe saves: merged into the stored arrangements, without those it overrides. */
export const toSavedArrangements = ({ recipe, viewId, state: { viewpointModeViewSettings, blocksGraphInputs }, root, }: ToSavedArrangementsArgs): BlocksArrangement[] => effectiveArrangements(blocksGraphInputs.loadedGraph.scopeData, root, mergeArrangements(parseArrangements(viewpointModeViewSettings[viewId]?.arrangements), recipe.arrangements));
type ValidateLayoutArgs = {
    viewId: ArrangeableViewId;
    rawPayload: unknown;
    state: CommandExecutionState;
    findGroupLinks: FindGroupLinks;
};
/** Checks a recipe for the view it was sent to, and works out what it saves and what it shows. */
export const validateLayout = ({ viewId, rawPayload, state, findGroupLinks, }: ValidateLayoutArgs): CommandValidationResult<ResolvedBlocksLayout> => {
    if (!(() => { const _io0 = (input: any): boolean => "string" === typeof input.title && (Array.isArray(input.arrangements) && input.arrangements.every((elem: any) => "object" === typeof elem && null !== elem && _io1(elem))); const _io1 = (input: any): boolean => (null === input.containerId || "string" === typeof input.containerId) && (Array.isArray(input.areas) && input.areas.every((elem: any) => "string" === typeof elem)) && (Array.isArray(input.regions) && input.regions.every((elem: any) => "object" === typeof elem && null !== elem && _io2(elem))); const _io2 = (input: any): boolean => "string" === typeof input.name && (Array.isArray(input.componentTypes) && input.componentTypes.every((elem: any) => "string" === typeof elem)) && (Array.isArray(input.labels) && input.labels.every((elem: any) => "string" === typeof elem)) && (Array.isArray(input.descendantsOf) && input.descendantsOf.every((elem: any) => "string" === typeof elem)) && "boolean" === typeof input.remaining && ("row" === input.flow || "column" === input.flow || "grid" === input.flow || "tree" === input.flow) && ("start" === input.align || "center" === input.align) && ("label" === input.sortBy || "flow" === input.sortBy || "none" === input.sortBy) && (null === input.columns || "number" === typeof input.columns) && (null === input.rows || "number" === typeof input.rows) && "boolean" === typeof input.stretch && (null === input.childLayout || "object" === typeof input.childLayout && null !== input.childLayout && _io3(input.childLayout)) && (null === input.alignWith || "object" === typeof input.alignWith && null !== input.alignWith && _io4(input.alignWith)); const _io3 = (input: any): boolean => ("row" === input.flow || "column" === input.flow || "grid" === input.flow) && (null === input.columns || "number" === typeof input.columns) && (null === input.rows || "number" === typeof input.rows) && ("start" === input.align || "center" === input.align) && ("label" === input.sortBy || "flow" === input.sortBy || "none" === input.sortBy); const _io4 = (input: any): boolean => "string" === typeof input.region && (Array.isArray(input.referenceTypes) && input.referenceTypes.every((elem: any) => "string" === typeof elem)); return (input: any): input is BlocksArrangementRecipe => "object" === typeof input && null !== input && _io0(input); })()(rawPayload)) {
        return { status: 'rejected', reason: describeInvalidRecipe(rawPayload) };
    }
    if (state.activeViewId !== viewId) {
        return {
            status: 'rejected',
            reason: 'The requested command is no longer available.',
        };
    }
    const recipe = parseArrangementRecipe(rawPayload);
    if (!recipe) {
        return {
            status: 'rejected',
            reason: 'The AI returned an invalid block layout.',
        };
    }
    const inputs = state.blocksGraphInputs;
    const { scopeData } = inputs.loadedGraph;
    const root = buildGroupTree(inputs);
    const resolved = resolveContainerArrangements({
        scopeData,
        root,
        locale: inputs.locale,
        arrangements: effectiveArrangements(scopeData, root, recipe.arrangements),
    });
    const alignmentProblems = describeAlignmentProblems(root, resolved);
    const expectedArrangements = toSavedArrangements({
        recipe,
        viewId,
        state,
        root,
    });
    if (alignmentProblems.length) {
        return {
            status: 'rejected',
            reason: `Not applied. ${alignmentProblems.join(' ')} Resend the layout without alignWith on that region, or with its areas fixed. Tell the user the blocks couldn't be lined up and why, and pass on any suggestion above.`,
        };
    }
    return {
        status: 'valid',
        payload: {
            recipe,
            expectedArrangements,
            report: describeArrangedLayout({
                root,
                resolved,
                groupLinks: findGroupLinks({
                    scopeData,
                    root,
                    resolved: resolveContainerArrangements({
                        scopeData,
                        root,
                        locale: inputs.locale,
                        arrangements: expectedArrangements,
                    }),
                }),
            }),
        },
    };
};
/** What both blocks views' layout commands share: one tool, one recipe, one context. */
export const LAYOUT_COMMAND_METADATA = {
    name: AiControlledCommandId.LAYOUT_BLOCKS,
    requiresAppShell: true,
    domain: VIEWPOINT_DOMAIN,
    parameters: {
        version: "3.1",
        components: {
            schemas: {
                BlocksArrangementRecipe: {
                    type: "object",
                    properties: {
                        title: {
                            type: "string",
                            description: "A short, descriptive title for the layout."
                        },
                        arrangements: {
                            type: "array",
                            items: {
                                $ref: "#/components/schemas/BlocksArrangement"
                            },
                            description: "One arrangement per group you want to lay out. Usually just the top level (containerId null)."
                        }
                    },
                    required: [
                        "title",
                        "arrangements"
                    ],
                    description: "A region-based layout for the graph the user is viewing in Assistant Blocks."
                },
                BlocksArrangement: {
                    type: "object",
                    properties: {
                        containerId: {
                            oneOf: [
                                {
                                    type: "null"
                                },
                                {
                                    type: "string"
                                }
                            ],
                            description: "The id of the group whose children to arrange, or null for the top level."
                        },
                        areas: {
                            type: "array",
                            items: {
                                type: "string"
                            },
                            description: "CSS grid-template-areas, one string per row, area names separated by spaces.\nRepeat a name to make its area span several columns or rows; every area must\nbe a rectangle. Use '.' for an empty cell. Example \u2014 a band across the top,\na column down the left and the rest beside it: [\"top top\", \"left main\"].\nAn empty list, with no regions, clears the group's arrangement."
                        },
                        regions: {
                            type: "array",
                            items: {
                                $ref: "#/components/schemas/BlocksRegion"
                            },
                            description: "One entry per area name used in `areas`."
                        }
                    },
                    required: [
                        "containerId",
                        "areas",
                        "regions"
                    ],
                    description: "An arrangement of one group's children."
                },
                BlocksRegion: {
                    type: "object",
                    properties: {
                        name: {
                            type: "string",
                            description: "The area name, exactly as written in `areas`."
                        },
                        componentTypes: {
                            type: "array",
                            items: {
                                type: "string"
                            },
                            description: "Component type names, exactly as given in the context. Nodes of these types go here."
                        },
                        labels: {
                            type: "array",
                            items: {
                                type: "string"
                            },
                            description: "Exact node labels, for a named group or a specific component. Nodes with these labels go here."
                        },
                        descendantsOf: {
                            type: "array",
                            items: {
                                type: "string"
                            },
                            description: "Labels of nodes whose whole subtree goes here: each named node and everything beneath it in the component hierarchy."
                        },
                        remaining: {
                            type: "boolean",
                            description: "When true, every node no other region claimed goes here. Use on at most one region."
                        },
                        flow: {
                            $ref: "#/components/schemas/RegionFlow",
                            description: "'row' lines the nodes up left to right, 'column' stacks them top to bottom, 'grid' wraps them\nto fill the area. A row or column of more than 12 wraps onto further rows (or columns) in a\nlandscape block. 'tree' draws each hierarchy as a capability map: the top node spanning a row,\nits children as columns, and their descendants stacked beneath them."
                        },
                        align: {
                            $ref: "#/components/schemas/RegionAlign",
                            description: "'start' packs the nodes into the top left of the area, 'center' centers them in it."
                        },
                        sortBy: {
                            $ref: "#/components/schemas/RegionSort",
                            description: "'label' orders the nodes alphabetically, 'flow' along the references between them (sources\nfirst, e.g. value stream stages by 'Flows To'), 'none' keeps their current order."
                        },
                        columns: {
                            oneOf: [
                                {
                                    type: "null"
                                },
                                {
                                    type: "number"
                                }
                            ],
                            description: "For 'grid' or 'row': exactly this many columns, e.g. 2, wrapping onto more rows. Null lets the shape decide."
                        },
                        rows: {
                            oneOf: [
                                {
                                    type: "null"
                                },
                                {
                                    type: "number"
                                }
                            ],
                            description: "For 'column', or 'grid' when columns is null: exactly this many rows."
                        },
                        stretch: {
                            type: "boolean",
                            description: "In a 'column' flow each node spans the area's full width; in a 'row' flow, its full height. Ignored for 'grid' and 'tree'."
                        },
                        childLayout: {
                            oneOf: [
                                {
                                    type: "null"
                                },
                                {
                                    $ref: "#/components/schemas/BlocksChildLayout"
                                }
                            ],
                            description: "How to lay out the children inside each group this region holds, e.g. two columns of\nchildren per group. Null leaves them to the default layout."
                        },
                        alignWith: {
                            oneOf: [
                                {
                                    type: "null"
                                },
                                {
                                    $ref: "#/components/schemas/BlocksAlignWith"
                                }
                            ],
                            description: "Line each block of this region up with the block in another region it is connected to by a\nreference. The template decides the direction: with this area below that region's, each block\ngoes in its related block's column, under it; above, over it; to the right or left, in its\nrow, beside it. Blocks connected to nothing there are placed after. The other region may itself\nbe aligned, for chains. Null when blocks are not placed by their references."
                        }
                    },
                    required: [
                        "name",
                        "componentTypes",
                        "labels",
                        "descendantsOf",
                        "remaining",
                        "flow",
                        "align",
                        "sortBy",
                        "columns",
                        "rows",
                        "stretch",
                        "childLayout",
                        "alignWith"
                    ],
                    description: "One named area of the template and the nodes that go in it."
                },
                RegionFlow: {
                    oneOf: [
                        {
                            "const": "row"
                        },
                        {
                            "const": "column"
                        },
                        {
                            "const": "grid"
                        },
                        {
                            "const": "tree"
                        }
                    ]
                },
                RegionAlign: {
                    oneOf: [
                        {
                            "const": "start"
                        },
                        {
                            "const": "center"
                        }
                    ]
                },
                RegionSort: {
                    oneOf: [
                        {
                            "const": "label"
                        },
                        {
                            "const": "flow"
                        },
                        {
                            "const": "none"
                        }
                    ]
                },
                BlocksChildLayout: {
                    type: "object",
                    properties: {
                        flow: {
                            $ref: "#/components/schemas/ChildFlow",
                            description: "'row', 'column' or 'grid', as for a region."
                        },
                        columns: {
                            oneOf: [
                                {
                                    type: "null"
                                },
                                {
                                    type: "number"
                                }
                            ],
                            description: "For 'grid': exactly this many columns. Null lets the shape decide."
                        },
                        rows: {
                            oneOf: [
                                {
                                    type: "null"
                                },
                                {
                                    type: "number"
                                }
                            ],
                            description: "For 'grid' when columns is null: exactly this many rows."
                        },
                        align: {
                            $ref: "#/components/schemas/RegionAlign"
                        },
                        sortBy: {
                            $ref: "#/components/schemas/RegionSort"
                        }
                    },
                    required: [
                        "flow",
                        "columns",
                        "rows",
                        "align",
                        "sortBy"
                    ],
                    description: "How the children inside a group are laid out."
                },
                ChildFlow: {
                    oneOf: [
                        {
                            "const": "row"
                        },
                        {
                            "const": "column"
                        },
                        {
                            "const": "grid"
                        }
                    ]
                },
                BlocksAlignWith: {
                    type: "object",
                    properties: {
                        region: {
                            type: "string",
                            description: "The name of the region whose blocks these line up with, e.g. a row of value stream stages."
                        },
                        referenceTypes: {
                            type: "array",
                            items: {
                                type: "string"
                            },
                            description: "Reference type names that count, exactly as given in the context. Empty counts any reference."
                        }
                    },
                    required: [
                        "region",
                        "referenceTypes"
                    ],
                    description: "Lines each block up with the block in another region it is connected to by a reference."
                }
            }
        },
        schema: {
            $ref: "#/components/schemas/BlocksArrangementRecipe"
        }
    } as import("typia").IJsonSchemaUnit<"3.1">,
    contextDescription: BLOCKS_CONTEXT_DESCRIPTION,
};
export const isBlocksCommandContext = (context: unknown): context is BlocksCommandContext => (() => { const _io0 = (input: any): boolean => Array.isArray(input.containers) && input.containers.every((elem: any) => "object" === typeof elem && null !== elem && _io1(elem)) && "number" === typeof input.omittedContainerCount && (Array.isArray(input.appliedArrangements) && input.appliedArrangements.every((elem: any) => "object" === typeof elem && null !== elem && _io6(elem))) && ("object" === typeof input.layoutSize && null !== input.layoutSize && _io12(input.layoutSize)) && (null === input.title || "string" === typeof input.title) && (Array.isArray(input.arrangements) && input.arrangements.every((elem: any) => "object" === typeof elem && null !== elem && _io13(elem))); const _io1 = (input: any): boolean => (null === input.id || "string" === typeof input.id) && "string" === typeof input.label && (null === input.parentId || "string" === typeof input.parentId) && "number" === typeof input.childCount && (Array.isArray(input.childComponentTypes) && input.childComponentTypes.every((elem: any) => "object" === typeof elem && null !== elem && _io2(elem))) && (Array.isArray(input.descendantComponentTypes) && input.descendantComponentTypes.every((elem: any) => "object" === typeof elem && null !== elem && _io2(elem))) && (Array.isArray(input.childReferenceTypes) && input.childReferenceTypes.every((elem: any) => "object" === typeof elem && null !== elem && _io3(elem))) && (Array.isArray(input.childLabels) && input.childLabels.every((elem: any) => "object" === typeof elem && null !== elem && _io4(elem))) && "number" === typeof input.omittedLabelCount && (Array.isArray(input.hierarchyRoots) && input.hierarchyRoots.every((elem: any) => "object" === typeof elem && null !== elem && _io5(elem))); const _io2 = (input: any): boolean => "string" === typeof input.name && "number" === typeof input.count; const _io3 = (input: any): boolean => "string" === typeof input.name && "number" === typeof input.count; const _io4 = (input: any): boolean => "string" === typeof input.label && (null === input.componentType || "string" === typeof input.componentType); const _io5 = (input: any): boolean => "string" === typeof input.label && "number" === typeof input.descendantCount && (Array.isArray(input.childLabels) && input.childLabels.every((elem: any) => "string" === typeof elem)); const _io6 = (input: any): boolean => (null === input.containerId || "string" === typeof input.containerId) && (Array.isArray(input.areas) && input.areas.every((elem: any) => "string" === typeof elem)) && (Array.isArray(input.regions) && input.regions.every((elem: any) => "object" === typeof elem && null !== elem && _io7(elem))) && ("object" === typeof input.outcome && null !== input.outcome && _io10(input.outcome)); const _io7 = (input: any): boolean => "string" === typeof input.name && (Array.isArray(input.componentTypes) && input.componentTypes.every((elem: any) => "string" === typeof elem)) && (Array.isArray(input.labels) && input.labels.every((elem: any) => "string" === typeof elem)) && (Array.isArray(input.descendantsOf) && input.descendantsOf.every((elem: any) => "string" === typeof elem)) && "boolean" === typeof input.remaining && ("row" === input.flow || "column" === input.flow || "grid" === input.flow || "tree" === input.flow) && ("start" === input.align || "center" === input.align) && ("label" === input.sortBy || "flow" === input.sortBy || "none" === input.sortBy) && (null === input.columns || "number" === typeof input.columns) && (null === input.rows || "number" === typeof input.rows) && "boolean" === typeof input.stretch && (null === input.childLayout || "object" === typeof input.childLayout && null !== input.childLayout && _io8(input.childLayout)) && (null === input.alignWith || "object" === typeof input.alignWith && null !== input.alignWith && _io9(input.alignWith)); const _io8 = (input: any): boolean => ("row" === input.flow || "column" === input.flow || "grid" === input.flow) && (null === input.columns || "number" === typeof input.columns) && (null === input.rows || "number" === typeof input.rows) && ("start" === input.align || "center" === input.align) && ("label" === input.sortBy || "flow" === input.sortBy || "none" === input.sortBy); const _io9 = (input: any): boolean => "string" === typeof input.region && (Array.isArray(input.referenceTypes) && input.referenceTypes.every((elem: any) => "string" === typeof elem)); const _io10 = (input: any): boolean => Array.isArray(input.regions) && input.regions.every((elem: any) => "object" === typeof elem && null !== elem && _io11(elem)) && "number" === typeof input.viaRemaining && "number" === typeof input.unplaced; const _io11 = (input: any): boolean => "string" === typeof input.name && "number" === typeof input.count && (Array.isArray(input.groups) && input.groups.every((elem: any) => "string" === typeof elem)) && (null === input.unrelated || "number" === typeof input.unrelated); const _io12 = (input: any): boolean => "number" === typeof input.blocksAcross && "number" === typeof input.blocksDown && "number" === typeof input.aspectRatio; const _io13 = (input: any): boolean => (null === input.containerId || "string" === typeof input.containerId) && (Array.isArray(input.areas) && input.areas.every((elem: any) => "string" === typeof elem)) && (Array.isArray(input.regions) && input.regions.every((elem: any) => "object" === typeof elem && null !== elem && _io7(elem))); return (input: any): input is BlocksCommandContext => "object" === typeof input && null !== input && _io0(input); })()(context);
